import type { Category } from "../../../redux/types/categoryTypes";
import { parsePriceCents } from "./pricing";
import {
  genderLabels,
  returnPolicies,
  SellingValues,
  selectListingCategory,
} from "./types";

export const stepFields: (keyof SellingValues)[][] = [
  [
    "title",
    "description",
    "gender",
    "categoryId",
    "subcategoryId",
    "upc",
    "gtin",
  ],
  ["condition", "quantity", "conditionNotes"],
  ["price", "minimumOffer"],
  ["shipping", "length", "width", "height", "pounds", "ounces", "returns"],
];
export function validateField(
  name: keyof SellingValues,
  values: SellingValues,
  categories: Category[],
): true | string {
  const value = values[name].trim();
  switch (name) {
    case "title":
      return !value
        ? "Enter a product title."
        : values.title.length > 60
          ? "Use 60 characters or fewer."
          : true;
    case "description":
      return values.description.length <= 300 || "Use 300 characters or fewer.";
    case "gender":
      return (
        Object.prototype.hasOwnProperty.call(genderLabels, value) ||
        "Choose Boys, Girls, or Gender Neutral."
      );
    case "categoryId":
      return (
        categories.some(
          (category) =>
            category.source === "catalogue" && category.id === value,
        ) || "Choose a main category from the catalogue."
      );
    case "subcategoryId":
      return (
        !!selectListingCategory(categories, values) ||
        "Choose a subcategory belonging to your main category."
      );
    case "condition":
      return ["new", "used"].includes(value) || "Choose New or Used.";
    case "quantity":
      return (
        (/^\d+$/.test(value) &&
          Number.isSafeInteger(Number(value)) &&
          Number(value) >= 1 &&
          (values.condition !== "used" || Number(value) === 1)) ||
        "Enter a positive whole quantity; Used toys must have quantity 1."
      );
    case "price":
      return (
        parsePriceCents(value) !== null ||
        "Enter a price greater than $0 with up to 2 decimal places."
      );
    case "minimumOffer": {
      if (!value) return true;
      const cents = parsePriceCents(value);
      const asking = parsePriceCents(values.price);
      return cents === null
        ? "Enter a positive offer with up to 2 decimal places."
        : asking === null || cents > asking
          ? "Your lowest offer cannot exceed the asking price."
          : true;
    }
    case "shipping":
      return (
        parsePriceCents(value, true) !== null ||
        "Enter a U.S. shipping charge of $0 or more, with up to 2 decimal places."
      );
    case "length":
    case "width":
    case "height":
      return (
        (/^\d+(\.\d{1,2})?$/.test(value) &&
          Number.isFinite(Number(value)) &&
          Number(value) > 0) ||
        "Enter the packed box measurement in inches, greater than zero (up to 2 decimal places)."
      );
    case "pounds":
      if (
        value &&
        (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)))
      )
        return "Enter whole pounds of zero or more.";
      return (
        Number(values.pounds || 0) * 16 + Number(values.ounces || 0) > 0 ||
        "Enter a packed weight greater than zero."
      );
    case "ounces":
      return (
        !value ||
        (/^\d+(\.\d{1,2})?$/.test(value) &&
          Number(value) >= 0 &&
          Number(value) < 16) ||
        "Enter ounces from 0 to less than 16; add whole pounds separately."
      );
    case "returns":
      return (
        Object.prototype.hasOwnProperty.call(returnPolicies, value) ||
        "Choose a return policy."
      );
    case "upc":
    case "gtin":
      return (
        value.length <= 128 || "Use an identifier of 128 characters or fewer."
      );
    case "conditionNotes":
      return value.length <= 1000 || "Use 1,000 characters or fewer.";
    default:
      return true;
  }
}

export function stepErrors(
  step: number,
  values: SellingValues,
  categories: Category[],
) {
  return (stepFields[step] || []).flatMap((name) => {
    const error = validateField(name, values, categories);
    return error === true ? [] : [{ name, message: error }];
  });
}
